<script setup>
defineProps({
  /** white, cream, sand, orange, ink, peach, tint */
  tone: { type: String, default: 'white' },
  /** none, sm, md, lg */
  pad: { type: String, default: 'md' },
  /** Grid column span inside PdBento (1 to 12) */
  span: { type: [Number, String], default: null },
  /** Grid row span inside PdBento */
  rows: { type: [Number, String], default: null },
  /** Render as link or button with hover lift */
  href: { type: String, default: null },
  interactive: { type: Boolean, default: false },
  /** Adds the thin concentric rings decoration, drawn as lines, not fills */
  rings: { type: Boolean, default: false },
  as: { type: String, default: 'section' },
});
</script>

<template>
  <component
    :is="href ? 'a' : as"
    :href="href || undefined"
    class="pd-tile"
    :class="[`pd-tile--${tone}`, `pd-tile--pad-${pad}`, { 'is-interactive': interactive || href }]"
    :style="{ gridColumn: span ? `span ${span}` : undefined, gridRow: rows ? `span ${rows}` : undefined }"
  >
    <svg v-if="rings" class="pd-tile__rings" viewBox="0 0 200 200" aria-hidden="true">
      <circle cx="200" cy="200" r="70" />
      <circle cx="200" cy="200" r="110" />
      <circle cx="200" cy="200" r="150" />
    </svg>
    <slot />
  </component>
</template>

<style scoped>
.pd-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border-radius: var(--pd-r-lg);
  border: 1px solid transparent;
  color: var(--pd-ink);
  text-decoration: none;
  isolation: isolate;
}
.pd-tile--pad-none { padding: 0; }
.pd-tile--pad-sm { padding: 18px; }
.pd-tile--pad-md { padding: var(--pd-tile-pad); }
.pd-tile--pad-lg { padding: calc(var(--pd-tile-pad) + 8px); }

.pd-tile--white { background: var(--pd-white); border-color: var(--pd-line); }
.pd-tile--cream { background: var(--pd-cream); border-color: var(--pd-line); }
.pd-tile--sand { background: var(--pd-sand); }
.pd-tile--orange { background: var(--pd-orange); color: var(--pd-on-orange); }
.pd-tile--peach { background: var(--pd-peach); }
.pd-tile--tint { background: var(--pd-tint); border-color: var(--pd-peach); }
.pd-tile--ink { background: var(--pd-ink); color: var(--pd-white); }

.is-interactive { cursor: pointer; transition: transform var(--pd-med) var(--pd-ease), border-color var(--pd-fast) var(--pd-ease); }
@media (hover: hover) {
  .is-interactive:hover { transform: translateY(-3px); }
  .pd-tile--white.is-interactive:hover { border-color: var(--pd-line-active); }
}
.is-interactive:active { transform: scale(0.99); }

.pd-tile__rings {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 70%;
  max-width: 320px;
  z-index: -1;
  fill: none;
  stroke: currentColor;
  stroke-width: 0.6;
  opacity: 0.12;
  pointer-events: none;
}
.pd-tile--ink .pd-tile__rings { stroke: var(--pd-orange); opacity: 0.28; }
</style>
